import { useEffect, useState } from 'react';
import { resolveAvatarSrc } from 'utils/avatar';

export const identityIds = (user) => [
  user?.tmAuthUserId, user?.userId, user?.id, user?._id, user?.localUserId,
].map((id) => String(id || '').trim()).filter(Boolean);

const ownAvatar = (user) => resolveAvatarSrc(user?.avatarUrl) || resolveAvatarSrc(user?.avatar);

// Shared avatar source for agenda items, activity participants and the time report:
// the freshest matching meeting user, then the user's own avatar, then the unified identity profile.
export default function useUserAvatar(user, meeting) {
  const participantIds = identityIds(user);
  const matchingUsers = participantIds.length ? [
    ...(meeting?.roleAssignments || []).map((assignment) => assignment?.user),
    ...(meeting?.items || []).map((item) => item?.assignedUser),
    ...(meeting?.meetingDetails?.attendance || []),
  ].filter((candidate) => identityIds(candidate).some((id) => participantIds.includes(id))) : [];
  const currentUser = matchingUsers.find((candidate) => ownAvatar(candidate)) || matchingUsers[0];
  const initialAvatar = ownAvatar(currentUser) || ownAvatar(user);
  const userId = identityIds(currentUser)[0] || participantIds[0] || '';
  const [avatar, setAvatar] = useState(initialAvatar);
  const [failedAvatar, setFailedAvatar] = useState(null);

  useEffect(() => {
    setAvatar(initialAvatar);
    setFailedAvatar(null);
  }, [initialAvatar, userId]);

  useEffect(() => {
    if ((initialAvatar && initialAvatar !== failedAvatar) || !userId) return undefined;

    const controller = new AbortController();
    const loadAvatar = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_URL}/unified/${encodeURIComponent(userId)}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Profile request failed (${response.status})`);
        const profile = await response.json();
        if (!controller.signal.aborted) {
          setAvatar(resolveAvatarSrc(profile?.identity?.avatarUrl || profile?.identity?.avatar));
        }
      } catch (error) {
        if (!controller.signal.aborted) console.warn('[useUserAvatar] Unable to load avatar:', error.message);
      }
    };
    void loadAvatar();
    return () => controller.abort();
  }, [initialAvatar, userId, failedAvatar]);

  return {
    avatarSrc: avatar && avatar !== failedAvatar ? avatar : null,
    onAvatarError: () => setFailedAvatar(avatar),
    displayUser: currentUser || user,
  };
}
